import React, {useEffect, useState} from "react";
import {css} from "@panda/css";
import {AnimatePresence, motion} from "framer-motion";

import {Button} from "@pg-design/button-module";
import {CloseIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {Modal} from "@pg-design/modal-module";
import {FormProviderRHF, MaxValueFieldControlled, SelectRangeFieldControlled, useFormRHF} from "@pg-design/rhf-components-module";
import {ISelectRangeOption} from "@pg-design/select-range-module";
import {Highlight, Text} from "@pg-design/text-module";

import {RegistrationMktOptins} from "../../../auth/components/auth_card/RegistrationMktOptins";
import {useGetRegionDetailListQuery} from "../../../region/api/get_region_detail_list";
import {SearchTab} from "../../../search/types/SearchTab";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {UserPreferencesRegionField} from "../../../user/components/preferences/UserPreferencesRegionField";
import {UserPreferencesRegionMap} from "../../../user/components/preferences/UserPreferencesRegionMap";
import {PREFERENCES_FORM_MAX_DEFAULT, PREFERENCES_FORM_STEP} from "../../../user/constants/preferences/application_preferences_form";
import {useNewsletterDefaultValues} from "../../hooks/useNewsletterDefaultValues";
import {useSubscribeToNewsletter} from "../../hooks/useSubscribeToNewsletter";
import {closeNewsletterSubscriptionModal} from "../../slice/newsletter_subscription_modal_slice";

const initialValues = {
    budget: PREFERENCES_FORM_MAX_DEFAULT,
    rooms: {lower: undefined, upper: undefined},
    marketing_email: false,
    marketing_sms: false,
    regionSelector: [] as {value: number; label: string}[],
    regions: [] as {value: number; label: string}[]
};

export const NewsletterSubscriptionModal = () => {
    const dispatch = useAppDispatch();
    const {defaultValues, loadingDefaultValues} = useNewsletterDefaultValues(initialValues);
    const formMethods = useFormRHF({defaultValues});
    const [marketingConsentsError, setMarketingConsentsError] = useState<string | undefined>();
    const [isLocationFocused, setIsLocationFocused] = useState(false);
    const isOpen = useAppSelector((state) => state.newsletterSubscriptionModal.isOpen);
    const {data: userProfile, isLoading: isUserProfileLoading} = useGetUserProfileQuery();
    const {subscribeToNotifications, getNewsletterSearchData, saveNewsletterSearchData} = useSubscribeToNewsletter();
    const newsletterSearchData = getNewsletterSearchData();
    const maxBudget = Math.max(5_000_000, newsletterSearchData?.price?.upper || 0);

    const shouldShowConsents = !userProfile?.email_marketing_consent || !userProfile?.phone_marketing_consent;
    const [marketingEmail, marketingSms] = formMethods.watch(["marketing_email", "marketing_sms"]);
    const regionSelector = formMethods.watch("regionSelector");
    const regionIds = regionSelector.map((r: {value: number}) => r.value);
    const {data: regionDetails, isLoading: isRegionDetailsLoading} = useGetRegionDetailListQuery({regionIds}, {skip: !regionIds.length});

    useEffect(() => {
        if (marketingConsentsError && (marketingEmail || marketingSms)) {
            setMarketingConsentsError(undefined);
            formMethods.clearErrors(["marketing_email", "marketing_sms"]);
        }
    }, [marketingEmail, marketingSms]);

    useEffect(() => {
        if (userProfile) {
            formMethods.reset({
                ...formMethods.getValues(),
                ...defaultValues
            });
        }
    }, [userProfile, defaultValues]);

    const closeModal = () => {
        dispatch(closeNewsletterSubscriptionModal());
    };

    const onSubmit = async (values: typeof defaultValues) => {
        if (shouldShowConsents && !values.marketing_email && !values.marketing_sms) {
            setMarketingConsentsError("Zaznacz przynajmniej jedną opcję żeby otrzymywać powiadomienia");
            formMethods.setError("marketing_email", {type: "manual", message: " "});
            formMethods.setError("marketing_sms", {type: "manual", message: " "});
            return;
        }

        if (userProfile) {
            const currentSearchData = getNewsletterSearchData() ?? {};
            saveNewsletterSearchData({
                ...currentSearchData,
                rooms: values.rooms,
                ...(values.budget ? {price: {upper: values.budget}} : {}),
                ...(regionDetails && regionDetails.length > 0
                    ? {
                          search: {
                              label: "",
                              tabType: SearchTab.Regions,
                              regions: regionDetails
                          }
                      }
                    : {})
            });

            const userData = {
                ...userProfile,
                email_marketing_consent: values.marketing_email,
                phone_marketing_consent: values.marketing_sms,
                general_marketing_consent: values.marketing_sms && values.marketing_email
            };

            await subscribeToNotifications(userData);
            closeModal();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onModalClose={closeModal}
            className={css({
                maxWidth: "544px"
            })}
        >
            <Modal.Header className={modalHeader} as="div" showCloseButton={false}>
                <CloseIcon size="1.2" wrapperSize="3.2" wrapperColor="#fff" wrapperType="circle" onClick={closeModal} className={closeIconStyle} />

                <Text variant="headline_3" as="p" mb={1}>
                    <Highlight>Powiadomienia są włączone!</Highlight>
                </Text>

                <Text variant="body_copy_2">Zmień swoje preferencje, aby lepiej dostosować wysyłane oferty.</Text>
            </Modal.Header>

            <Modal.Content className={modalContent}>
                <FormProviderRHF {...formMethods}>
                    <form onSubmit={formMethods.handleSubmit(onSubmit)} className={formStyle}>
                        {isUserProfileLoading || loadingDefaultValues ? (
                            <Loader size="lg" />
                        ) : (
                            <>
                                <UserPreferencesRegionField
                                    name="regionSelector"
                                    label="Lokalizacja"
                                    onInputClick={(defaultAction) => {
                                        defaultAction();
                                        setIsLocationFocused(true);
                                    }}
                                />

                                <AnimatePresence>
                                    {isLocationFocused && (
                                        <motion.div
                                            initial={{opacity: 0, height: 0}}
                                            animate={{opacity: 1, height: "auto"}}
                                            exit={{opacity: 0, height: 0}}
                                            transition={transition}
                                        >
                                            <div className={mapHolder}>
                                                <UserPreferencesRegionMap onActionButtonClick={() => setIsLocationFocused(false)} />
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                <motion.div
                                    animate={{
                                        opacity: isLocationFocused ? 0 : 1,
                                        height: isLocationFocused ? 0 : "auto",
                                        overflow: isLocationFocused ? "hidden" : "auto"
                                    }}
                                    className={formStyle}
                                    transition={transition}
                                >
                                    <MaxValueFieldControlled
                                        name="budget"
                                        min={100000}
                                        max={maxBudget}
                                        step={PREFERENCES_FORM_STEP}
                                        rightElement="zł"
                                        labelContent="Budżet"
                                        maxDisplayLabel="bez limitu"
                                    />

                                    <div className={css({mt: 3})}>
                                        <Text variant="body_copy_2" mb={1}>
                                            Liczba pokoi
                                        </Text>

                                        <SelectRangeFieldControlled name="rooms" options={roomOptions} />
                                    </div>

                                    {shouldShowConsents && <RegistrationMktOptins customError={marketingConsentsError} />}

                                    <Button type="submit" variant="filled_primary" size="medium" className={css({mt: 3})} isLoading={isRegionDetailsLoading}>
                                        Zapisz zmiany
                                    </Button>
                                </motion.div>
                            </>
                        )}
                    </form>
                </FormProviderRHF>
            </Modal.Content>
        </Modal>
    );
};

const transition = {
    type: "spring",
    stiffness: 300,
    damping: 30
};

const roomOptions: ISelectRangeOption[] = [
    {id: "1", label: "1", value: 1},
    {id: "2", label: "2", value: 2},
    {id: "3", label: "3", value: 3},
    {id: "4", label: "4", value: 4},
    {id: "5", label: "5+", value: 5}
];

const modalPaddingString = "calc(var(--globals-baseUnit) * 3)";

const modalHeader = css({
    pt: modalPaddingString,
    px: modalPaddingString,
    mr: 6,
    mb: 3
});

const closeIconStyle = css({
    position: "absolute",
    top: 2,
    right: 2,
    boxShadow: "2",
    cursor: "pointer"
});

const modalContent = css({
    p: modalPaddingString
});

const formStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 3
});

const mapHolder = css({
    minHeight: "460px"
});
import React, {useEffect, useState} from "react";
import {css} from "@panda/css";
import {AnimatePresence, motion} from "framer-motion";

import {Button} from "@pg-design/button-module";
import {CloseIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {Modal} from "@pg-design/modal-module";
import {FormProviderRHF, MaxValueFieldControlled, SelectRangeFieldControlled, useFormRHF} from "@pg-design/rhf-components-module";
import {ISelectRangeOption} from "@pg-design/select-range-module";
import {Highlight, Text} from "@pg-design/text-module";

import {RegistrationMktOptins} from "../../../auth/components/auth_card/RegistrationMktOptins";
import {useGetRegionDetailListQuery} from "../../../region/api/get_region_detail_list";
import {SearchTab} from "../../../search/types/SearchTab";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {UserPreferencesRegionField} from "../../../user/components/preferences/UserPreferencesRegionField";
import {UserPreferencesRegionMap} from "../../../user/components/preferences/UserPreferencesRegionMap";
import {PREFERENCES_FORM_MAX_DEFAULT, PREFERENCES_FORM_STEP} from "../../../user/constants/preferences/application_preferences_form";
import {useNewsletterDefaultValues} from "../../hooks/useNewsletterDefaultValues";
import {useSubscribeToNewsletter} from "../../hooks/useSubscribeToNewsletter";
import {closeNewsletterSubscriptionModal} from "../../slice/newsletter_subscription_modal_slice";

const initialValues = {
    budget: PREFERENCES_FORM_MAX_DEFAULT,
    rooms: {lower: undefined, upper: undefined},
    marketing_email: false,
    marketing_sms: false,
    regionSelector: [] as {value: number; label: string}[],
    regions: [] as {value: number; label: string}[]
};

export const NewsletterSubscriptionModal = () => {
    const dispatch = useAppDispatch();
    const {defaultValues, loadingDefaultValues} = useNewsletterDefaultValues(initialValues);
    const formMethods = useFormRHF({defaultValues});
    const [marketingConsentsError, setMarketingConsentsError] = useState<string | undefined>();
    const [isLocationFocused, setIsLocationFocused] = useState(false);
    const isOpen = useAppSelector((state) => state.newsletterSubscriptionModal.isOpen);
    const {data: userProfile, isLoading: isUserProfileLoading} = useGetUserProfileQuery();
    const {subscribeToNotifications, getNewsletterSearchData, saveNewsletterSearchData} = useSubscribeToNewsletter();
    const newsletterSearchData = getNewsletterSearchData();
    const maxBudget = Math.max(5_000_000, newsletterSearchData?.price?.upper || 0);

    const shouldShowConsents = !userProfile?.email_marketing_consent || !userProfile?.phone_marketing_consent;
    const [marketingEmail, marketingSms] = formMethods.watch(["marketing_email", "marketing_sms"]);
    const regionSelector = formMethods.watch("regionSelector");
    const regionIds = regionSelector.map((r: {value: number}) => r.value);
    const {data: regionDetails, isLoading: isRegionDetailsLoading} = useGetRegionDetailListQuery({regionIds}, {skip: !regionIds.length});

    useEffect(() => {
        if (marketingConsentsError && (marketingEmail || marketingSms)) {
            setMarketingConsentsError(undefined);
            formMethods.clearErrors(["marketing_email", "marketing_sms"]);
        }
    }, [marketingEmail, marketingSms]);

    useEffect(() => {
        if (userProfile) {
            formMethods.reset({
                ...formMethods.getValues(),
                ...defaultValues
            });
        }
    }, [userProfile, defaultValues]);

    const closeModal = () => {
        dispatch(closeNewsletterSubscriptionModal());
    };

    const onSubmit = async (values: typeof defaultValues) => {
        if (shouldShowConsents && !values.marketing_email && !values.marketing_sms) {
            setMarketingConsentsError("Zaznacz przynajmniej jedną opcję żeby otrzymywać powiadomienia");
            formMethods.setError("marketing_email", {type: "manual", message: " "});
            formMethods.setError("marketing_sms", {type: "manual", message: " "});
            return;
        }

        if (userProfile) {
            const currentSearchData = getNewsletterSearchData() ?? {};
            saveNewsletterSearchData({
                ...currentSearchData,
                rooms: values.rooms,
                ...(values.budget ? {price: {upper: values.budget}} : {}),
                ...(regionDetails && regionDetails.length > 0
                    ? {
                          search: {
                              label: "",
                              tabType: SearchTab.Regions,
                              regions: regionDetails
                          }
                      }
                    : {})
            });

            const userData = {
                ...userProfile,
                email_marketing_consent: values.marketing_email,
                phone_marketing_consent: values.marketing_sms,
                general_marketing_consent: values.marketing_sms && values.marketing_email
            };

            await subscribeToNotifications(userData);
            closeModal();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onModalClose={closeModal}
            className={css({
                maxWidth: "544px"
            })}
        >
            <Modal.Header className={modalHeader} as="div" showCloseButton={false}>
                <CloseIcon size="1.2" wrapperSize="3.2" wrapperColor="#fff" wrapperType="circle" onClick={closeModal} className={closeIconStyle} />

                <Text variant="headline_3" as="p" mb={1}>
                    <Highlight>Powiadomienia są włączone!</Highlight>
                </Text>

                <Text variant="body_copy_2">Zmień swoje preferencje, aby lepiej dostosować wysyłane oferty.</Text>
            </Modal.Header>

            <Modal.Content className={modalContent}>
                <FormProviderRHF {...formMethods}>
                    <form onSubmit={formMethods.handleSubmit(onSubmit)} className={formStyle}>
                        {isUserProfileLoading || loadingDefaultValues ? (
                            <Loader size="lg" />
                        ) : (
                            <>
                                <UserPreferencesRegionField
                                    name="regionSelector"
                                    label="Lokalizacja"
                                    onInputClick={(defaultAction) => {
                                        defaultAction();
                                        setIsLocationFocused(true);
                                    }}
                                />

                                <AnimatePresence>
                                    {isLocationFocused && (
                                        <motion.div
                                            initial={{opacity: 0, height: 0}}
                                            animate={{opacity: 1, height: "auto"}}
                                            exit={{opacity: 0, height: 0}}
                                            transition={transition}
                                        >
                                            <div className={mapHolder}>
                                                <UserPreferencesRegionMap onActionButtonClick={() => setIsLocationFocused(false)} />
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                <motion.div
                                    animate={{
                                        opacity: isLocationFocused ? 0 : 1,
                                        height: isLocationFocused ? 0 : "auto",
                                        overflow: isLocationFocused ? "hidden" : "auto"
                                    }}
                                    className={formStyle}
                                    transition={transition}
                                >
                                    <MaxValueFieldControlled
                                        name="budget"
                                        min={100000}
                                        max={maxBudget}
                                        step={PREFERENCES_FORM_STEP}
                                        rightElement="zł"
                                        labelContent="Budżet"
                                        maxDisplayLabel="bez limitu"
                                    />

                                    <div className={css({mt: 3})}>
                                        <Text variant="body_copy_2" mb={1}>
                                            Liczba pokoi
                                        </Text>

                                        <SelectRangeFieldControlled name="rooms" options={roomOptions} />
                                    </div>

                                    {shouldShowConsents && <RegistrationMktOptins customError={marketingConsentsError} />}

                                    <Button type="submit" variant="filled_primary" size="medium" className={css({mt: 3})} isLoading={isRegionDetailsLoading}>
                                        Zapisz zmiany
                                    </Button>
                                </motion.div>
                            </>
                        )}
                    </form>
                </FormProviderRHF>
            </Modal.Content>
        </Modal>
    );
};

const transition = {
    type: "spring",
    stiffness: 300,
    damping: 30
};

const roomOptions: ISelectRangeOption[] = [
    {id: "1", label: "1", value: 1},
    {id: "2", label: "2", value: 2},
    {id: "3", label: "3", value: 3},
    {id: "4", label: "4", value: 4},
    {id: "5", label: "5+", value: 5}
];

const modalPaddingString = "calc(var(--globals-baseUnit) * 3)";

const modalHeader = css({
    pt: modalPaddingString,
    px: modalPaddingString,
    mr: 6,
    mb: 3
});

const closeIconStyle = css({
    position: "absolute",
    top: 2,
    right: 2,
    boxShadow: "2",
    cursor: "pointer"
});

const modalContent = css({
    p: modalPaddingString
});

const formStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 3
});

const mapHolder = css({
    minHeight: "460px"
});
